declare global {
  interface Window {
    __APP_CONFIG__?: {
      VITE_API_URL?: string;
      VITE_PUBLIC_SUPABASE_URL?: string;
      VITE_PUBLIC_SUPABASE_ANON_KEY?: string;
    };
  }
}

const runtimeConfig = typeof window !== 'undefined' ? window.__APP_CONFIG__ : undefined;

const API_URL =
  runtimeConfig?.VITE_API_URL ||
  (import.meta.env.VITE_API_URL as string) ||
  'http://localhost:3000';

export const APP_DATA_VERSION_KEY = 'neozoo:data-version';
export const AUTH_TOKEN_KEY = 'neozoo:session-token';
export const AUTH_EXPIRED_EVENT = 'neozoo:auth-expired';

export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export function isApiErrorStatus(error: unknown, status: number): boolean {
  return error instanceof ApiError && error.status === status;
}

function notifyOtherTabsAboutDataChange() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(APP_DATA_VERSION_KEY, `${Date.now()}:${crypto.randomUUID()}`);
  } catch {
    // La operación principal ya se guardó. La sincronización también se
    // recupera cuando la otra pestaña vuelve a tomar foco.
  }
}

async function request<T>(
  path: string,
  options: globalThis.RequestInit = {},
): Promise<T> {
  const token = typeof window !== 'undefined' ? window.sessionStorage.getItem(AUTH_TOKEN_KEY) : null;
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers ?? {}),
    },
  });

  if (!res.ok) {
    const body = await res.text().catch(() => res.statusText);
    let message = '';
    try {
      const parsed = JSON.parse(body) as { message?: string | string[] };
      message = Array.isArray(parsed.message) ? parsed.message.join(' ') : String(parsed.message || '');
    } catch {
      message = '';
    }
    if (res.status === 401 && path !== '/auth/login' && typeof window !== 'undefined') {
      window.sessionStorage.removeItem(AUTH_TOKEN_KEY);
      window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
    }
    throw new ApiError(message || (res.status >= 500
      ? 'No pudimos completar la operación en este momento. Intentá nuevamente.'
      : 'No se pudo completar la operación. Revisá los datos ingresados.'), res.status);
  }

  const method = String(options.method ?? 'GET').toUpperCase();
  if (method !== 'GET' && method !== 'HEAD') notifyOtherTabsAboutDataChange();
  if (res.status === 204) return undefined as T;
  const responseBody = await res.text();
  if (!responseBody.trim()) return null as T;
  return JSON.parse(responseBody) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body), keepalive: true }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body), keepalive: true }),
  delete: (path: string) => request<void>(path, { method: 'DELETE', keepalive: true }),
  upload: <T>(path: string, file: File) => {
    const body = new FormData();
    body.append('file', file);
    return request<T>(path, { method: 'POST', body });
  },
  blob: async (path: string) => {
    const token = typeof window !== 'undefined' ? window.sessionStorage.getItem(AUTH_TOKEN_KEY) : null;
    const response = await fetch(`${API_URL}${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!response.ok) throw new ApiError('No se pudo abrir el archivo.', response.status);
    return response.blob();
  },
};
